import { Link } from "react-router-dom";
import { Check, ArrowRight, Phone } from "lucide-react";
import { PageLayout } from "./PageLayout";
import { Reveal } from "./Reveal";
import { Faq } from "./Faq";
import { JsonLd } from "./JsonLd";
import { CtaBanner } from "./CtaBanner";
import { CompareSlider } from "./BeforeAfter";
import { PROCESS, WHY_US, CONTACT } from "../data/content";

const btnPrimary =
    "inline-flex items-center justify-center gap-2.5 bg-leaf hover:bg-forest text-white font-semibold px-7 py-4 rounded-full text-base transition-colors duration-200";
const btnGhost =
    "inline-flex items-center justify-center gap-2.5 border border-forest/25 text-forest font-semibold px-7 py-4 rounded-full text-base transition-colors duration-200 hover:bg-leaf-light";

export const ServicePage = ({ eyebrow, h1, intro, image, imageAlt, scope, sections, pair, sliderTitle, faq, serviceType, related }) => (
    <PageLayout>
        <JsonLd
            data={{
                "@context": "https://schema.org",
                "@type": "Service",
                serviceType,
                name: h1,
                provider: {
                    "@type": "HomeAndConstructionBusiness",
                    name: "Julians Gartenservice",
                    url: "https://www.julian-gartenservice.de/",
                },
                areaServed: "Landkreis Celle",
            }}
        />
        {faq?.length > 0 && (
            <JsonLd
                data={{
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: faq.map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                }}
            />
        )}

        <section className="pt-28 lg:pt-40 pb-16 lg:pb-20 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <Reveal>
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-leaf font-semibold mb-4">{eyebrow}</p>
                    <h1 className="font-display font-bold tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">{h1}</h1>
                    <p className="mt-6 text-base sm:text-lg text-shade leading-relaxed">{intro}</p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
                        <Link to="/angebot" data-testid="service-hero-quote" className={btnPrimary}>
                            Kostenloses Angebot anfragen
                            <ArrowRight size={17} strokeWidth={2.4} />
                        </Link>
                        <a href={CONTACT.phoneHref} data-testid="service-hero-call" className={btnGhost}>
                            <Phone size={16} strokeWidth={2.4} />
                            {CONTACT.phoneDisplay}
                        </a>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <img
                        src={image}
                        alt={imageAlt}
                        className="w-full aspect-[4/3] object-cover rounded-3xl shadow-[0_30px_80px_-30px_rgba(24,59,43,0.4)]"
                        loading="eager"
                    />
                </Reveal>
            </div>
        </section>

        <section className="bg-mist py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <Reveal>
                    <h2 className="font-display font-semibold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl">
                        Das übernehme ich
                    </h2>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 max-w-4xl">
                    {scope.map((s) => (
                        <Reveal key={s}>
                            <div className="flex items-start gap-3 text-base text-ink">
                                <span className="mt-1 w-6 h-6 rounded-full bg-leaf/15 text-leaf flex items-center justify-center shrink-0">
                                    <Check size={14} strokeWidth={3} />
                                </span>
                                {s}
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-12 max-w-4xl mx-auto">
            {sections.map((sec) => (
                <Reveal key={sec.title} className="mb-12 last:mb-0">
                    <h2 className="font-display font-semibold tracking-tight text-ink text-2xl sm:text-3xl">{sec.title}</h2>
                    {sec.paragraphs.map((p) => (
                        <p key={p.slice(0, 40)} className="mt-4 text-base sm:text-lg text-shade leading-relaxed">{p}</p>
                    ))}
                </Reveal>
            ))}
            {related?.length > 0 && (
                <Reveal>
                    <div className="mt-12 pt-8 border-t border-hairline">
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-fog mb-4">Passende Leistungen</p>
                        <div className="flex flex-wrap gap-2.5">
                            {related.map((r) => (
                                <Link
                                    key={r.to}
                                    to={r.to}
                                    data-testid={`related-${r.to.slice(1)}`}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-hairline text-sm font-medium text-forest transition-colors duration-200 hover:bg-leaf-light hover:border-leaf/30"
                                >
                                    {r.label}
                                    <ArrowRight size={13} />
                                </Link>
                            ))}
                        </div>
                    </div>
                </Reveal>
            )}
        </section>

        {pair && (
            <section className="relative bg-forest-deep grain py-16 lg:py-24 overflow-hidden">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
                    <Reveal>
                        <h2 className="font-display font-semibold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl">
                            {sliderTitle}
                        </h2>
                        <p className="mt-4 text-base sm:text-lg text-white/60 max-w-xl leading-relaxed">
                            Ziehen Sie den Regler und sehen Sie den Unterschied.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-8">
                        <CompareSlider pair={pair} />
                        <p className="mt-5 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
                            {pair.caption}
                        </p>
                    </Reveal>
                </div>
            </section>
        )}

        <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
            <Reveal>
                <h2 className="font-display font-semibold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl">
                    Darauf können Sie sich verlassen
                </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-hairline rounded-3xl overflow-hidden border border-hairline">
                {WHY_US.map((w) => (
                    <div key={w.num} className="h-full bg-white p-7">
                        <span className="font-mono text-3xl font-semibold text-leaf/30">{w.num}</span>
                        <h3 className="mt-4 font-display font-semibold tracking-tight text-ink text-lg">{w.title}</h3>
                        <p className="mt-2 text-sm text-shade leading-relaxed">{w.desc}</p>
                    </div>
                ))}
            </div>
        </section>

        <section className="bg-mist py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <Reveal>
                    <h2 className="font-display font-semibold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl">
                        So läuft die Zusammenarbeit
                    </h2>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {PROCESS.map((p) => (
                        <Reveal key={p.step}>
                            <div className="w-12 h-12 rounded-2xl bg-forest text-leaf-soft font-mono font-semibold flex items-center justify-center text-sm">
                                {p.step}
                            </div>
                            <h3 className="mt-5 font-display font-semibold tracking-tight text-ink text-lg">{p.title}</h3>
                            <p className="mt-2 text-sm text-shade leading-relaxed">{p.desc}</p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {faq?.length > 0 && (
            <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-12 max-w-4xl mx-auto">
                <Reveal>
                    <h2 className="font-display font-semibold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl mb-10">
                        Häufige Fragen
                    </h2>
                </Reveal>
                <Faq items={faq} />
            </section>
        )}

        <CtaBanner />
    </PageLayout>
);
